import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  fieldProblems,
  financeError,
  useSaveInvoice,
} from '../../hooks/useFinance';
import ConsultantPicker from './ConsultantPicker';
import {
  AMOUNT_RE,
  FILE_ACCEPT,
  FILE_KINDS,
  INPUT,
  KRA_PIN_RE,
  PRIMARY,
  SECONDARY,
  currentPeriod,
  fileProblem,
} from './financeText';

function Field({ label, hint, error, children }) {
  return (
    <div className="flex flex-col gap-1">
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label className="text-xs font-medium text-gray-700">
        {label}
        {children}
      </label>
      {hint && !error && (
        <span className="text-[11px] text-gray-500">{hint}</span>
      )}
      {error && <span className="text-[11px] text-red-700">{error}</span>}
    </div>
  );
}

// Create (no invoice) or edit. `scope` 'my' is the consultant's own;
// 'all' is a finance holder, who picks the consultant when creating.
export default function InvoiceForm({ scope, invoice, onDone, onCancel }) {
  const save = useSaveInvoice(scope);
  const editing = Boolean(invoice);
  const [values, setValues] = useState({
    period: invoice?.period ?? '',
    invoice_number: invoice?.invoice_number ?? '',
    amount: invoice?.amount ?? '',
    etims_invoice_number: invoice?.etims_invoice_number ?? '',
    kra_pin: invoice?.kra_pin ?? '',
  });
  const [consultant, setConsultant] = useState(null);
  const [files, setFiles] = useState({});
  const [serverProblems, setServerProblems] = useState({});
  const maxPeriod = currentPeriod();

  const set = (key) => (e) => {
    const value =
      key === 'kra_pin' ? e.target.value.toUpperCase() : e.target.value;
    setValues((v) => ({ ...v, [key]: value }));
    setServerProblems((p) => ({ ...p, [key]: undefined }));
  };

  const problems = {
    period:
      values.period && values.period > maxPeriod
        ? 'The month can’t be in the future.'
        : null,
    amount:
      values.amount && !AMOUNT_RE.test(values.amount)
        ? 'Enter an amount like 45000 or 45000.50.'
        : null,
    kra_pin:
      values.kra_pin && !KRA_PIN_RE.test(values.kra_pin)
        ? 'A KRA PIN looks like A012345678Z.'
        : null,
    invoice_file: fileProblem(files.invoice_file),
    etims_file: fileProblem(files.etims_file),
  };
  const missing =
    !values.period ||
    !values.invoice_number.trim() ||
    !values.amount ||
    !values.etims_invoice_number.trim() ||
    !values.kra_pin ||
    (!editing && (!files.invoice_file || !files.etims_file)) ||
    (!editing && scope === 'all' && !consultant);
  const blocked = missing || Object.values(problems).some(Boolean);

  const submit = (e) => {
    e.preventDefault();
    if (blocked) return;
    const form = new FormData();
    Object.entries(values).forEach(([key, value]) =>
      form.append(key, String(value).trim())
    );
    if (!editing && scope === 'all')
      form.append('administrator_id', consultant.administrator_id);
    FILE_KINDS.forEach(({ field }) => {
      if (files[field]) form.append(field, files[field], files[field].name);
    });
    save.mutate(
      { id: invoice?.id, form },
      {
        onSuccess: (saved) => {
          toast.success(editing ? 'Invoice updated.' : 'Invoice submitted.');
          onDone(saved);
        },
        onError: (error) => {
          setServerProblems(fieldProblems(error));
          toast.error(financeError(error, "We couldn't save the invoice."));
        },
      }
    );
  };

  const errorFor = (key) => problems[key] || serverProblems[key];
  const submitLabel = editing ? 'Save changes' : 'Submit invoice';

  return (
    <form onSubmit={submit} className="space-y-4 text-left">
      {!editing && scope === 'all' && (
        <Field label="Consultant" error={serverProblems.administrator_id}>
          <ConsultantPicker value={consultant} onChange={setConsultant} />
        </Field>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Month" error={errorFor('period')}>
          <input
            type="month"
            max={maxPeriod}
            value={values.period}
            onChange={set('period')}
            className={`${INPUT} mt-1 w-full`}
          />
        </Field>
        <Field
          label="Amount (KES)"
          hint="e.g. 45000 or 45000.50"
          error={errorFor('amount')}
        >
          <input
            type="text"
            inputMode="decimal"
            value={values.amount}
            onChange={set('amount')}
            className={`${INPUT} mt-1 w-full`}
          />
        </Field>
        <Field label="Invoice number" error={errorFor('invoice_number')}>
          <input
            type="text"
            value={values.invoice_number}
            onChange={set('invoice_number')}
            className={`${INPUT} mt-1 w-full`}
          />
        </Field>
        <Field
          label="eTIMS invoice number"
          error={errorFor('etims_invoice_number')}
        >
          <input
            type="text"
            value={values.etims_invoice_number}
            onChange={set('etims_invoice_number')}
            className={`${INPUT} mt-1 w-full`}
          />
        </Field>
        <Field
          label="KRA PIN"
          hint="e.g. A012345678Z"
          error={errorFor('kra_pin')}
        >
          <input
            type="text"
            value={values.kra_pin}
            onChange={set('kra_pin')}
            maxLength={11}
            className={`${INPUT} mt-1 w-full font-mono`}
          />
        </Field>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {FILE_KINDS.map(({ field, label }) => (
          <Field
            key={field}
            label={`${label}${
              editing ? ' (optional: replaces the current file)' : ''
            }`}
            hint="PDF, JPG or PNG, up to 10 MB"
            error={errorFor(field)}
          >
            <input
              type="file"
              accept={FILE_ACCEPT}
              aria-label={label}
              onChange={(e) =>
                setFiles((f) => ({ ...f, [field]: e.target.files?.[0] }))
              }
              className="mt-1 block w-full text-xs text-gray-700"
            />
          </Field>
        ))}
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel} className={SECONDARY}>
          Cancel
        </button>
        <button
          type="submit"
          disabled={blocked || save.isLoading}
          className={PRIMARY}
        >
          {save.isLoading ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
